import { DataTable, useContainerHeight , EmptyState } from '@components/DataTable';
import { DataTableColumn, SelectionModel, SortModel } from '@components/DataTable/types';
import ScoringReadinessBadge from '@modules/scoring/components/ScoringReadinessBadge';
import ScoringScoreBadge from '@modules/scoring/components/ScoringScoreBadge';
import TalentMediaDialog from '@modules/talent-media/TalentMediaDialog';
import { Box, Grid, Skeleton, TableCell, TableRow, Typography } from '@mui/material';
import { format } from 'date-fns';
import React, { useCallback, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { TalentActionsCell, TalentCellData, TalentNameCell, TalentRatingCell } from './components/TalentCell';
import { useTalentOperations } from './context/TalentOperationsContext';
import { MultiSelectTalent } from './type';
import { ScoringStatus } from '@modules/scoring/types';

interface TalentDataTableProps {
    cardMenuList: {
        id: number;
        title: string;
        icon: React.ElementType;
        type: string;
        style?: string;
        path?: string;
        actionTitle?: string;
        actionDescription?: string;
        confirmTitle?: string;
        cancelTitle?: string;
        action?: string;
        description?: string;
        actionType?: string;
    }[];
    data: {
        _id: string;
        imageUrl: string;
        fullName: string;
        jobOffer: string;
        brand: string;
        city: string;
        rating: number;
        origin: string;
        totalApplied: number;
        appliedJobs: number;
        experience: string;
        contractType: string;
        education: string;
        company: string;
        logo: string;
        active: boolean;
        currentStatusUser?: string;
    }[];
    headerColumnList: {
        id: number;
        title: React.ReactNode;
        minWidth?: string;
    }[];
    loading: boolean;
    scoringResultsByTalentId?: Record<string, { score?: number | null; status: 'pending' | 'in_progress' | 'done' | 'error'; explainer?: string }>;
    showScoringColumn?: boolean;
    onOpenScoringDialog?: (talentId: string, data: { score?: number | null; status: 'pending' | 'in_progress' | 'done' | 'error'; explainer?: string }) => void;
    selectedTalents: MultiSelectTalent[];
    sortModel?: SortModel[];
    onSortModelChange?: (model: SortModel[]) => void;
    pagination?: {
        currentPage: number;
        pageSize: number;
        totalRows: number;
        onPageChange: (page: number) => void;
        onPageSizeChange: (pageSize: number) => void;
    };
    isLoadingMore?: boolean;
    onScrollEnd?: () => void;
    onReuploadSuccess?: () => void;
}

/**
 * Talent row skeleton component - matches the structure of talent columns
 */
const TalentRowSkeleton = () => (
    <TableRow>
        {/* Checkbox column */}
        <TableCell sx={{ width: 58, padding: '6px 8px' }}>
            <Skeleton variant="rectangular" width={24} height={24} />
        </TableCell>
        {/* Name column */}
        <TableCell sx={{ padding: '6px 8px' }}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Skeleton variant="circular" width={40} height={40} />
                <Box sx={{ flex: 1 }}>
                    <Skeleton variant="text" width="80%" height={16} />
                </Box>
            </Box>
        </TableCell>
        {/* Rating column */}
        <TableCell sx={{ padding: '6px 8px', minWidth: 180 }}>
            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                <Skeleton variant="rectangular" width={80} height={20} />
                <Skeleton variant="text" width={40} height={16} />
            </Box>
        </TableCell>
        {/* City column */}
        <TableCell sx={{ padding: '6px 8px', minWidth: 120 }}>
            <Skeleton variant="text" width="70%" height={16} />
        </TableCell>
        {/* Experience column */}
        <TableCell sx={{ padding: '6px 8px', minWidth: 120 }}>
            <Skeleton variant="text" width="60%" height={16} />
        </TableCell>
        {/* Contract column */}
        <TableCell sx={{ padding: '6px 8px', minWidth: 100 }}>
            <Skeleton variant="text" width="75%" height={16} />
        </TableCell>
        {/* Education column */}
        <TableCell sx={{ padding: '6px 8px', minWidth: 100 }}>
            <Skeleton variant="text" width="75%" height={16} />
        </TableCell>
        {/* Applications column */}
        <TableCell sx={{ padding: '6px 8px', minWidth: 100 }}>
            <Skeleton variant="text" width="40%" height={16} />
        </TableCell>
        {/* Created date column */}
        <TableCell sx={{ padding: '6px 8px', minWidth: 140 }}>
            <Skeleton variant="text" width="85%" height={16} />
        </TableCell>
        {/* Actions column */}
        <TableCell sx={{ padding: '6px 8px', minWidth: 170 }}>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Skeleton variant="circular" width={32} height={32} />
                <Skeleton variant="circular" width={32} height={32} />
            </Box>
        </TableCell>
    </TableRow>
);

const TalentDataTable = ({
    data,
    loading,
    scoringResultsByTalentId,
    showScoringColumn = false,
    onOpenScoringDialog,
    selectedTalents,
    sortModel,
    onSortModelChange,
    pagination,
    isLoadingMore,
    onScrollEnd,
    onReuploadSuccess,
}: TalentDataTableProps) => {
    const navigate = useNavigate();
    const { clearSelection, toggleTalent } = useTalentOperations();

    const [mediaDialogState, setMediaDialogState] = useState<{
        isOpen: boolean;
        talentId: string;
        defaultTab: 'notes' | 'files' | 'comments';
        score?: number | null;
        scoreExplainer?: string;
        scoringStatus?: ScoringStatus;
    }>({
        isOpen: false,
        talentId: '',
        defaultTab: 'notes',
    });

    const handleCloseMediaDialog = useCallback(() => {
        setMediaDialogState(prev => ({
            ...prev,
            isOpen: false,
        }));
    }, []);

    /** Map raw row data into a normalised TalentCellData shape */
    const toTalentCell = useCallback((row: any): TalentCellData => {
        const scoring = scoringResultsByTalentId?.[row._id];
        return {
            _id: row._id,
            fullName: `${row.firstName || ''} ${row.lastName || ''}`.trim(),
            photo: row.profilePicture?.url || '',
            active: row.active,
            cvUserData: row,
            currentStatusUser: row.currentStatusUser,
            upercutAverageRating: row.upercutAverageRating,
            upercutNotes: row.upercutNotes,
            upercutFiles: row.upercutFiles,
            upercutFavorites: row.upercutFavorites,
            onOpenMedia: (defaultTab) => setMediaDialogState({
                isOpen: true,
                talentId: row._id,
                defaultTab,
                score: scoring?.score,
                scoreExplainer: scoring?.explainer,
                scoringStatus: scoring?.status,
            }),
        };
    }, [scoringResultsByTalentId]);

    const selectionModel = useMemo<SelectionModel>(() => {
        const selectedRows = data
            .filter(row => selectedTalents.some(selected => selected._id === row._id))
            .map(row => row._id);

        return {
            selectedRows,
            isAllSelected: data.length > 0 && selectedRows.length === data.length,
            isSomeSelected: selectedRows.length > 0 && selectedRows.length < data.length,
        };
    }, [data, selectedTalents]);

    const handleSelectionModelChange = (newSelection: SelectionModel) => {
        if (newSelection.selectedRows.length === 0) {
            clearSelection();
            return;
        }

        const nextSelectedIds = new Set(
            newSelection.selectedRows.filter((id): id is string => typeof id === 'string')
        );

        data.forEach(row => {
            const isCurrentlySelected = selectedTalents.some(selected => selected._id === row._id);
            const shouldBeSelected = nextSelectedIds.has(row._id);

            if (isCurrentlySelected !== shouldBeSelected) {
                toggleTalent(row._id, row.currentStatusUser);
            }
        });
    };

    const columns = useMemo<DataTableColumn<any>[]>(() => {
        const baseColumns: DataTableColumn<any>[] = [
            {
                id: 'name',
                field: 'fullName',
                sortable: true,
                label: 'Nom',
                sticky: true,
                renderCell: ({ row }) => <TalentNameCell talent={toTalentCell(row)} />,
            },
            {
                id: 'rating',
                field: 'upercutAverageRating',
                sortable: true,
                label: 'Évaluation',
                minWidth: 180,
                renderCell: ({ row }) => <TalentRatingCell talent={toTalentCell(row)} />,
            },
            {
                id: 'city',
                field: 'city',
                sortable: true,
                label: 'Ville',
                minWidth: 120,
                renderCell: ({ row }) => row.city || '-',
            },
            {
                id: 'experience',
                field: 'yearsOfExperience',
                sortable: true,
                label: 'Expérience',
                minWidth: 120,
                renderCell: ({ row }) => (typeof row.yearsOfExperience === 'number' ? `${Math.floor(row.yearsOfExperience)} ans` : '-'),
            },
            {
                id: 'preferences',
                sortable: false,
                label: 'Contrat',
                minWidth: 100,
                resizable: true,
                wrap: true,
                renderCell: ({ row }) => {
                    const preferences = row.preferences || [];
                    return preferences.length > 0 ? preferences.map((p: any) => p).join(', ') : '-';
                },
            },
            {
                id: 'education',
                sortable: false,
                label: 'Étude',
                minWidth: 100,
            },
            {
                id: 'totalApplications',
                field: 'totalApplications',
                sortable: true,
                label: 'Application',
                minWidth: 100,
                renderCell: ({ row }) => <>
                    <Typography variant="body2" color="text.primary" component={Link} to={`/talents/${row._id}/applications`} sx={{ 
                        textDecoration: 'none',
                        fontWeight: 700,
                        "&:hover": {
                            textDecoration: 'underline',
                        }
                    }}>
                        {row.jobApplications?.length || 0}
                    </Typography>
                </>,
            },
            {
                id: 'createdAt',
                field: 'createdAt',
                sortable: true,
                label: 'Créé',
                minWidth: 140,
                renderCell: ({ row }) => (row.createdAt ? format(row.createdAt, 'dd.MM.yy(HH:mm)') : '-'),
            },
            {
                id: 'actions',
                label: '',
                minWidth: 170,
                stickyRight: true,
                renderCell: ({ row }) => (
                    <TalentActionsCell
                        talent={toTalentCell(row)}
                        onView={(t) => {
                            if (row.active) {
                                navigate(`/talents/${t._id}/detail`, { state: { data: row } });
                            }
                        }}
                    />
                ),
            },
        ];

        if (showScoringColumn) {
            baseColumns.splice(2, 0, {
                id: 'scoring',
                field: 'scoring',
                sortable: true,
                label: 'Scoring',
                minWidth: 120,
                renderCell: ({ row }) => {
                    const scoringResult = scoringResultsByTalentId?.[row._id];

                    if (scoringResult) {
                        return (
                            <ScoringScoreBadge
                                score={scoringResult.score}
                                status={scoringResult.status}
                                scoreExplainer={scoringResult.explainer}
                                onViewMoreClick={onOpenScoringDialog ? () => onOpenScoringDialog(row._id, { score: scoringResult.score, status: scoringResult.status, explainer: scoringResult.explainer }) : undefined}
                            />
                        );
                    }

                    if (typeof row.jobOfferScoring === 'number') {
                        return (
                            <ScoringScoreBadge
                                score={row.jobOfferScoring}
                                status={row.jobOfferScoringStatus || 'done'}
                                scoreExplainer={row.jobOfferScoringExplainer}
                                onViewMoreClick={onOpenScoringDialog ? () => onOpenScoringDialog(row._id, { score: row.jobOfferScoring, status: row.jobOfferScoringStatus || 'done', explainer: row.jobOfferScoringExplainer }) : undefined}
                            />
                        );
                    }

                    return '-';
                },
            });

            baseColumns.splice(3, 0, {
                id: 'readiness',
                sortable: false,
                label: 'Disponibilité',
                minWidth: 160,
                renderCell: ({ row }) => (
                    <ScoringReadinessBadge
                        resumeId={row.resumeId}
                        resumePath={row.resume?.path}
                        talentId={row._id}
                        talentSlug={row.slug}
                        onReuploadSuccess={onReuploadSuccess}
                    />
                ),
            });
        }

        return baseColumns;
    }, [navigate, onOpenScoringDialog, onReuploadSuccess, scoringResultsByTalentId, showScoringColumn, toTalentCell]);

    const { containerHeight, containerRef } = useContainerHeight();

    return (
        <Box ref={containerRef} className="talent-list-table" 
            sx={{
                flex: 1, width: '100%',
                "& .MuiTableHead-root": {
                    zIndex: 103
                },
                // second cell from row should have overflow visible to show the full name and the company name
                '& .MuiTableRow-root': {
                    '& .MuiTableCell-root:nth-child(2)': {
                        zIndex: 102,
                        overflow: 'visible !important',
                        "& > .MuiBox-root": {
                            overflow: 'visible !important',
                        }
                    },
                },
            }}
        >
            <DataTable
                rows={data}
                columns={columns}
                loading={loading}
                sortModel={sortModel}
                onSortModelChange={onSortModelChange}
                pagination={pagination}
                checkboxSelection
                selectionModel={selectionModel}
                onSelectionModelChange={handleSelectionModelChange}
                stickyHeader={true}
                height={containerHeight}
                isLoadingMore={isLoadingMore}
                onScrollEnd={onScrollEnd}
                isRowSelectable={showScoringColumn ? (row) => Boolean(row.resumeId) : undefined}
                aria-label="job table"
                rowSkeleton={<TalentRowSkeleton />}
                noDataComponent={<Grid item xs={12}>
                    <EmptyState
                        title="Nous n'avons actuellement aucun candidat"
                        subtitle="Commençons par inviter de nouveaux talents à s'inscrire sur Upercut."
                    />
                </Grid>}
            />
            <TalentMediaDialog
                isOpen={mediaDialogState.isOpen}
                onClose={handleCloseMediaDialog}
                talentId={mediaDialogState.talentId}
                defaultTab={mediaDialogState.defaultTab}
                score={mediaDialogState.score}
                scoreExplainer={mediaDialogState.scoreExplainer}
                scoringStatus={mediaDialogState.scoringStatus}
            />
        </Box>
    );
};

export default TalentDataTable;
